import { captureExecution, currentAccountScope, sameAccount } from '@/analytics/context';
import { filterSnapshot } from '@/analytics/features/filters';
import {
  beginSearch,
  contextForPageRequest,
  navigationMethod,
  resultCount,
  resultsPageForResponse,
} from '@/analytics/features/search-ui';
import { executionForResponse } from '@/analytics/requests';
import { peopleResultCount } from '@/pages/Search/people/model';
import {
  fetchPerson as fetchPersonDetail,
  fetchPhone,
  searchPeople,
} from '@/pages/Search/people/service';
import {
  getPeopleCreditsAfterUnlock,
  isPeopleEmailUnlocked,
  isPeoplePhoneUnlocked,
} from '@/pages/Search/people/sharedUnlock';
import { getRequiredCredits, handlePeopleCreditError } from '@/utils/credits';
import type { AnyAction } from '@umijs/max';
import { message } from 'antd';
import { researchEntity } from './researchAnalytics';
import {
  researchRequest,
  revisitResearchPage,
  unavailableResearchPage,
  type ResearchView,
} from './researchContext';
import { getCompanyById, getCompanyPeople, getCompanyProducts } from './service';

const empty = () => ({
  scope: '',
  account: '',
  researchView: undefined as ResearchView | undefined,
  company: null as any,
  loading: false,
  companyFailed: false,
  companyExecution: undefined as any,
  companyLoadSource: 'network',
  peopleData: { pages: {} as Record<number, any>, total: 0 },
  peopleSearch: undefined as any,
  peopleDisplay: undefined as any,
  peopleRequest: undefined as any,
  peopleError: false,
  peopleFailedPage: 1,
  currentPeoplePage: 1,
  peoplePageSize: 10,
  contactAttempts: {} as Record<string, any>,
  productsData: [] as any[],
  productsTotal: 0,
  productsTotalAll: 0,
  productsMatchedTotal: 0,
  productsHaveMatchCriteria: false,
  productsLoading: false,
  productsError: false,
  productsDisplay: undefined as any,
  productSearches: {} as Record<string, any>,
  productsRequest: undefined as any,
  activeTab: 'products',
});
type State = ReturnType<typeof empty> & { cache: Record<string, ReturnType<typeof empty>> };
const initial = (): State => ({ ...empty(), cache: {} });
const active = (state: State, view?: ResearchView) =>
  state.researchView === view &&
  (!view?.context?.accountScope || sameAccount(view.context.accountScope, currentAccountScope()));
const saveCache = (state: State) => {
  const { cache, ...entry } = state;
  return state.scope ? { ...cache, [state.scope]: entry } : cache;
};

const model = {
  namespace: 'companyDetail',
  state: initial(),
  effects: {
    *fetchCompany(
      { payload, researchView }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const scope = payload.scope || String(payload.id);
      yield put({
        type: 'activateCompany',
        payload: { scope, account: payload.account || '', researchView },
      });
      let state: State = yield select((root: any) => root.companyDetail);
      if (state.company) {
        yield put({
          type: 'updateState',
          payload: { companyLoadSource: 'cache', companyExecution: researchView?.execution },
        });
        const cachedPeopleDisplay = state.peopleData.pages[state.currentPeoplePage]?.display;
        if (cachedPeopleDisplay && state.peopleSearch) {
          const request = contextForPageRequest(state.peopleSearch, {
            phase: 'page',
            trigger: 'automatic_batch',
            page: state.currentPeoplePage,
            pageSize: state.peoplePageSize,
            execution: researchView?.execution,
          });
          yield put({
            type: 'updateState',
            payload: { peopleDisplay: revisitResearchPage(cachedPeopleDisplay, request) },
          });
        }
        if (!state.peopleSearch)
          yield put({ type: 'fetchPeople', payload: { organizationId: payload.id } });
        else if (!cachedPeopleDisplay)
          yield put({
            type: 'changePeoplePage',
            payload: { page: state.currentPeoplePage, automatic: true },
          });
        return;
      }
      const prepared = researchRequest(researchView);
      yield put({
        type: 'updateState',
        payload: { loading: true, companyFailed: false, companyExecution: prepared.execution },
      });
      try {
        const company = yield call(
          getCompanyById,
          payload.id,
          prepared.analyticsContext,
          payload.signal ? { signal: payload.signal } : undefined,
        );
        state = yield select((root: any) => root.companyDetail);
        if (!active(state, researchView) || state.scope !== scope) return;
        yield put({
          type: 'updateState',
          payload: {
            company,
            loading: false,
            companyExecution: executionForResponse(company) || prepared.execution,
          },
        });
        if (company?.id)
          yield put({ type: 'fetchPeople', payload: { organizationId: company.id } });
      } catch {
        state = yield select((root: any) => root.companyDetail);
        if (!active(state, researchView) || state.scope !== scope) return;
        yield put({ type: 'updateState', payload: { loading: false, companyFailed: true } });
        if (!payload.signal?.aborted) message.error('Failed to fetch company details');
      }
    },
    *fetchPeople({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
      const state: State = yield select((root: any) => root.companyDetail);
      const view = state.researchView;
      if (state.company?.id !== payload.organizationId || state.peopleSearch) return;
      const run = beginSearch({
        surface: 'company_people_tab',
        params: { organizations: [payload.organizationId], role_is_current: true },
        criteria: filterSnapshot(
          { organizations: [payload.organizationId], role_is_current: true },
          {
            labels: { organizations: { [payload.organizationId]: state.company.name } },
            provenance: { organizations: 'system_scope', role_is_current: 'system_scope' },
          },
        ),
        context: view?.context,
        parentEntity: researchEntity(
          'forager_organization',
          payload.organizationId,
          state.company.name,
        ),
        execution: view?.execution,
        trigger: 'initial_load',
      });
      const request = contextForPageRequest(run, {
        phase: 'submit',
        trigger: 'initial_load',
        page: 1,
        pageSize: state.peoplePageSize,
      });
      const captured = request.analyticsContext
        ? captureExecution(request.analyticsContext)
        : undefined;
      yield put({
        type: 'updateState',
        payload: { peopleSearch: run, peopleRequest: request, peopleError: false },
      });
      try {
        const response = yield call(
          getCompanyPeople,
          payload.organizationId,
          request.analyticsContext,
        );
        const current: State = yield select((root: any) => root.companyDetail);
        if (
          !active(current, view) ||
          current.peopleSearch?.id !== run.id ||
          current.peopleRequest !== request
        )
          return;
        const display = resultsPageForResponse(run, request, response, peopleResultCount(response));
        yield put({
          type: 'setPeopleForPage',
          payload: {
            page: 1,
            data: response.search_results || [],
            total: response.total_search_results,
            display,
          },
        });
        yield put({
          type: 'updateState',
          payload: { currentPeoplePage: 1, peopleDisplay: display },
        });
      } catch {
        const current: State = yield select((root: any) => root.companyDetail);
        if (
          !active(current, view) ||
          current.peopleSearch?.id !== run.id ||
          current.peopleRequest !== request
        )
          return;
        yield put({
          type: 'updateState',
          payload: {
            peopleError: true,
            peopleFailedPage: 1,
            peopleDisplay: unavailableResearchPage(run, request, captured),
          },
        });
        message.error('Failed to fetch people data');
      }
    },
    *changePeoplePage(
      { payload }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const state: State = yield select((root: any) => root.companyDetail);
      const view = state.researchView;
      const run = state.peopleSearch;
      if (!state.company || !run) return;
      const page = payload.page;
      if (
        !payload.retry &&
        page === state.currentPeoplePage &&
        state.peopleData.pages[page] &&
        !state.peopleError
      )
        return;
      const request = contextForPageRequest(run, {
        phase: 'page',
        trigger: payload.automatic
          ? 'automatic_batch'
          : payload.retry
          ? 'retry'
          : 'user_pagination',
        page,
        pageSize: state.peoplePageSize,
        previousPage: state.currentPeoplePage,
        execution: view?.execution,
        navigationMethod:
          payload.retry || payload.automatic
            ? undefined
            : navigationMethod(state.currentPeoplePage, page),
      });
      const captured = request.analyticsContext
        ? captureExecution(request.analyticsContext)
        : undefined;
      yield put({ type: 'updateState', payload: { peopleRequest: request } });
      const cached = !payload.retry && state.peopleData.pages[page];
      if (cached) {
        yield put({
          type: 'updateState',
          payload: {
            currentPeoplePage: page,
            peopleError: false,
            peopleDisplay: revisitResearchPage(cached.display, request),
          },
        });
        return;
      }
      try {
        const response =
          page === 1
            ? yield call(getCompanyPeople, state.company.id, request.analyticsContext)
            : yield call(
                searchPeople,
                { organizations: [state.company.id], page, role_is_current: true },
                request.analyticsContext,
              );
        const current: State = yield select((root: any) => root.companyDetail);
        if (
          !active(current, view) ||
          current.peopleSearch?.id !== run.id ||
          current.peopleRequest !== request
        )
          return;
        const display = resultsPageForResponse(run, request, response, peopleResultCount(response));
        yield put({
          type: 'setPeopleForPage',
          payload: {
            page,
            data: response.search_results || [],
            total: response.total_search_results,
            display,
          },
        });
        yield put({
          type: 'updateState',
          payload: { currentPeoplePage: page, peopleError: false, peopleDisplay: display },
        });
      } catch {
        const current: State = yield select((root: any) => root.companyDetail);
        if (!active(current, view) || current.peopleRequest !== request) return;
        yield put({
          type: 'updateState',
          payload: {
            peopleError: true,
            peopleFailedPage: page,
            peopleDisplay: unavailableResearchPage(run, request, captured),
          },
        });
        message.error('Failed to fetch people data for page');
      }
    },
    *fetchProducts(
      { payload, researchView, searchIntent }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const state: State = yield select((root: any) => root.companyDetail);
      const view = researchView || state.researchView;
      if (state.company?.id !== payload.foragerId || (researchView && !active(state, researchView)))
        return;
      const { foragerId, take = 10, skip = 0, ...criteria } = payload;
      const key = JSON.stringify(criteria);
      const page = Math.floor(skip / take) + 1;
      // StrictMode replays the same committed read. A changed query, view or
      // explicit user action still owns its own request.
      if (
        !searchIntent?.trigger &&
        state.productsLoading &&
        active(state, view) &&
        state.productsRequest?.key === key &&
        state.productsRequest.request.page === page &&
        state.productsRequest.request.pageSize === take
      )
        return;
      let entry =
        searchIntent?.trigger === 'apply_filters' ? undefined : state.productSearches[key];
      const fresh = !entry;
      if (!entry)
        entry = {
          run: beginSearch({
            surface: 'company_products_tab',
            params: criteria,
            criteria: filterSnapshot(
              {
                organizations: [foragerId],
                ingredient_ids: payload.ingredientIds,
                client_category_ids: payload.categoryId ? [payload.categoryId] : [],
                ...payload.productFilters,
              },
              searchIntent?.filterMetadata || {
                labels: { organizations: { [foragerId]: state.company.name } },
                provenance: { organizations: 'system_scope', ingredient_ids: 'portfolio' },
              },
            ),
            context: view?.context,
            parentEntity: researchEntity('forager_organization', foragerId, state.company.name),
            execution: view?.execution,
            trigger: searchIntent?.trigger === 'apply_filters' ? 'apply_filters' : 'initial_load',
          }),
          pages: {},
        };
      const run = entry.run;
      const pageKey = `${take}:${page}`;
      const trigger = searchIntent?.trigger || (fresh ? 'initial_load' : 'automatic_batch');
      const request = contextForPageRequest(run, {
        phase: fresh ? 'submit' : 'page',
        trigger,
        page,
        pageSize: take,
        previousPage: searchIntent?.previousPage,
        navigationMethod: searchIntent?.navigationMethod,
        execution: view?.execution || run.execution,
      });
      const requested = { key, request, run };
      const captured = request.analyticsContext
        ? captureExecution(request.analyticsContext)
        : undefined;
      yield put({
        type: 'updateState',
        payload: {
          productsLoading: true,
          productsError: false,
          productsDisplay: undefined,
          productsRequest: requested,
          productSearches: { ...state.productSearches, [key]: entry },
        },
      });
      try {
        const cached = searchIntent?.trigger === 'retry' ? undefined : entry.pages[pageKey];
        const response =
          cached ||
          (yield call(
            getCompanyProducts,
            foragerId,
            { take, skip, ...criteria },
            request.analyticsContext,
          ));
        const current: any = yield select((root: any) => root.companyDetail);
        if (!active(current, view) || current.productsRequest !== requested) return;
        const display = cached
          ? revisitResearchPage(cached.display, request)
          : resultsPageForResponse(
              run,
              request,
              response,
              resultCount(Array.isArray(response) ? null : response.total),
            );
        const raw = cached ? cached.response : response;
        yield put({ type: 'setProducts', payload: raw });
        yield put({
          type: 'updateState',
          payload: {
            productsLoading: false,
            productsDisplay: display,
            productSearches: {
              ...current.productSearches,
              [key]: { run, pages: { ...entry.pages, [pageKey]: { response: raw, display } } },
            },
          },
        });
      } catch {
        const current: any = yield select((root: any) => root.companyDetail);
        if (!active(current, view) || current.productsRequest !== requested) return;
        yield put({
          type: 'updateState',
          payload: {
            productsLoading: false,
            productsError: true,
            productsData: [],
            productsDisplay: unavailableResearchPage(run, request, captured),
          },
        });
        message.error('Failed to fetch products');
      }
    },
    *fetchPerson(
      { callback, payload, isFreePlan, attempt }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const state: State = yield select((root: any) => root.companyDetail);
      const view = state.researchView;
      const person = Object.values(state.peopleData.pages)
        .flatMap((page: any) => page.data)
        .find((item: any) => item.person_id === payload);
      if (person && isPeopleEmailUnlocked(person) && person.work_email) {
        callback?.({ ...person, already_unlocked: true });
        return;
      }
      try {
        const subscription = yield select((root: any) => root.subscription);
        const response = yield call(
          fetchPersonDetail,
          payload,
          isFreePlan,
          attempt?.analyticsContext,
        );
        const current: State = yield select((root: any) => root.companyDetail);
        if (!active(current, view)) return;
        yield put({
          type: 'mergeContact',
          payload: {
            id: payload,
            data: { ...response, viewed: true, email_unlocked: response.success === true },
          },
        });
        callback?.(response, executionForResponse(response));
        if (response.work_email)
          yield put({
            type: 'subscription/deductCredits',
            payload: {
              people_credits_remaining: getPeopleCreditsAfterUnlock(
                response,
                subscription.people_credits_remaining,
                getRequiredCredits('email', subscription),
              ),
            },
          });
      } catch (error) {
        const current: State = yield select((root: any) => root.companyDetail);
        if (!active(current, view)) return;
        callback?.({ success: false });
        if (!handlePeopleCreditError(error, 'email')) message.error('Failed to fetch person data');
      }
    },
    *fetchPhone(
      { callback, payload, attempt }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const state: State = yield select((root: any) => root.companyDetail);
      const view = state.researchView;
      if (isPeoplePhoneUnlocked(payload) && payload.phone_numbers?.length) {
        callback?.({ ...payload, already_unlocked: true });
        return;
      }
      try {
        const subscription = yield select((root: any) => root.subscription);
        const response = yield call(fetchPhone, payload, attempt?.analyticsContext);
        const current: State = yield select((root: any) => root.companyDetail);
        if (!active(current, view)) return;
        if (response.success) {
          yield put({
            type: 'mergeContact',
            payload: {
              id: payload.person_id,
              data: { phone_numbers: response.phone_numbers, viewed: true, phone_unlocked: true },
            },
          });
          yield put({
            type: 'subscription/deductCredits',
            payload: {
              people_credits_remaining: getPeopleCreditsAfterUnlock(
                response,
                subscription.people_credits_remaining,
                getRequiredCredits('phone', subscription),
              ),
            },
          });
        } else message.error('No mobile number found');
        callback?.(response, executionForResponse(response));
      } catch (error) {
        const current: State = yield select((root: any) => root.companyDetail);
        if (!active(current, view)) return;
        callback?.({ success: false });
        if (!handlePeopleCreditError(error, 'phone')) message.error('No mobile number found');
      }
    },
  },
  reducers: {
    activateCompany(state: State, { payload }: AnyAction): State {
      if (state.scope === payload.scope && state.researchView === payload.researchView)
        return state;
      const cache = state.account === payload.account ? saveCache(state) : {};
      return {
        ...empty(),
        ...cache[payload.scope],
        productsData: [],
        productsDisplay: undefined,
        productsError: false,
        productsRequest: undefined,
        productsLoading: !!cache[payload.scope]?.company,
        peopleDisplay: undefined,
        peopleError: false,
        cache,
        scope: payload.scope,
        account: payload.account,
        researchView: payload.researchView,
      };
    },
    updateState(state: State, { payload }: AnyAction): State {
      return { ...state, ...payload };
    },
    setCompany(state: State, { payload }: AnyAction): State {
      return { ...state, company: payload };
    },
    setPeopleForPage(state: State, { payload }: AnyAction): State {
      return {
        ...state,
        peopleData: {
          pages: {
            ...state.peopleData.pages,
            [payload.page]: { data: payload.data, total: payload.total, display: payload.display },
          },
          total: payload.total ?? state.peopleData.total,
        },
      };
    },
    mergeContact(state: State, { payload }: AnyAction): State {
      return {
        ...state,
        peopleData: {
          ...state.peopleData,
          pages: Object.fromEntries(
            Object.entries(state.peopleData.pages).map(([page, value]) => [
              page,
              {
                ...value,
                data: value.data.map((person: any) =>
                  person.person_id === payload.id ? { ...person, ...payload.data } : person,
                ),
              },
            ]),
          ),
        },
      };
    },
    setActiveTab(state: State, { payload }: AnyAction): State {
      return { ...state, activeTab: payload };
    },
    setProducts(state: State, { payload }: AnyAction): State {
      const list = Array.isArray(payload);
      return {
        ...state,
        productsData: list ? payload : payload.data || [],
        productsTotal: list ? payload.length : payload.total || 0,
        productsTotalAll: list ? payload.length : payload.totalAll ?? payload.total ?? 0,
        productsMatchedTotal: list ? 0 : payload.matchedTotal ?? payload.total ?? 0,
        productsHaveMatchCriteria: !list && payload.hasMatchCriteria === true,
      };
    },
    setContactAttempt(state: State, { payload }: AnyAction): State {
      if (payload.view !== state.researchView) return state;
      return {
        ...state,
        contactAttempts: { ...state.contactAttempts, [payload.key]: payload.attempt },
      };
    },
    resetState(state: State, { payload }: AnyAction): State {
      if (payload && payload !== state.researchView) return state;
      return { ...empty(), account: state.account, cache: saveCache(state) };
    },
  },
};
export default model;
